import RadioFormComponent from "components/shared/FormComponent/RadioFormComponent";
import SelectFormComponent from "components/shared/FormComponent/SelectFormComponent";
import TextFormComponent from "components/shared/FormComponent/TextFormComponent";
import StyledButton from "components/shared/StyledButton";
import { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { CSSTransition } from "react-transition-group";
import type { IService } from "store/servicesStore";
import { createService, updateService } from "store/servicesStore";
import { useAppDispatch } from "store/store";

import styles from "./style.module.scss";

interface IServicesForm {
    /**
     * Create or edit type of form.
     */
    type: string;
    /**
     * Service data needed on edit.
     */
    service?: IService | undefined;
    /**
     * Triggers closing of modal.
     * @param service On article form, send back new service created to add it to data.
     */
    handleClose: (service?: IService) => void;
    /**
     * From which form the modal is being accessed.
     */
    from: "services" | "articleForm";
}

type FormValues = {
    name: string;
    racd: number;
    raap: number;
    sacd: number;
    hasIrcec: number;
};

/**
 * Form to create/update services.
 */
const ServicesForm = ({ type, service, handleClose, from }: IServicesForm) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const isUpdating = type === "edit";

    const [isSubmitting, setIsSubmitting] = useState(false);
    const blockRef = useRef<HTMLDivElement | null>(null);

    const defaultValues: FormValues = {
        name: isUpdating ? service?.name ?? "" : "",
        racd: isUpdating ? service?.racd ?? 0 : 0,
        raap: isUpdating ? service?.raap ?? 0 : 0,
        sacd: isUpdating ? service?.sacd ?? 0 : 0,
        hasIrcec: isUpdating ? service?.hasIrcec ?? 0 : 0,
    };

    const {
        control,
        formState: { errors },
        watch,
        handleSubmit,
    } = useForm<FormValues>({ defaultValues, mode: "all" });

    const ircecOptions = [{ label: t("ConnectTitle.No"), defaultChecked: true }, { label: t("ConnectTitle.Yes") }];

    const racdOptions = [
        { value: 0, label: "0%" },
        { value: 8, label: "6%" },
    ];

    const raapOptions = [
        { value: 0, label: "0%" },
        { value: 4, label: "3%" },
        { value: 8, label: "6%" },
    ];

    const sacdOptions = [
        { value: 0, label: "0%" },
        { value: 1, label: "1%" },
    ];

    const ircecApplies = +(watch("hasIrcec") ?? 0) === 1;

    const onSubmit = async (data: FormValues) => {
        setIsSubmitting(true);

        if (isUpdating && service) {
            const response = await dispatch(updateService({ serviceId: service.id, data }));
            if (response.meta.requestStatus === "fulfilled") {
                handleClose();
                setIsSubmitting(false);
            } else {
                setIsSubmitting(false);
            }
        } else {
            const response = await dispatch(createService({ data }));
            if (response.meta.requestStatus === "fulfilled") {
                from === "articleForm" ? handleClose(response.payload) : handleClose();
                setIsSubmitting(false);
            } else {
                setIsSubmitting(false);
            }
        }
    };

    return (
        <form className={styles["services-form-container"]} onSubmit={handleSubmit(onSubmit)}>
            <TextFormComponent
                control={control}
                controlName="name"
                label={t("Services.Name")}
                required
                error={errors.name}
            />
            <RadioFormComponent
                control={control}
                controlName="hasIrcec"
                options={ircecOptions}
                label={t("Services.HasIrcec")}
                error={errors.hasIrcec}
                required
            />
            <CSSTransition
                in={!!ircecApplies}
                timeout={500}
                classNames="ircec-conditional-block"
                unmountOnExit
                nodeRef={blockRef}
            >
                <div ref={blockRef} className={styles["ircec-conditional-block"]}>
                    <span className={styles["ircec-info"]}>{t("Services.Form.ChooseRate")}</span>
                    <SelectFormComponent
                        control={control}
                        controlName="racd"
                        label={t("Services.Form.Rate", { value: "RACD" })}
                        items={racdOptions}
                        error={errors.racd}
                        required
                    />
                    <SelectFormComponent
                        control={control}
                        controlName="raap"
                        label={t("Services.Form.Rate", { value: "RAAP" })}
                        items={raapOptions}
                        error={errors.raap}
                        required
                    />
                    <SelectFormComponent
                        control={control}
                        controlName="sacd"
                        label={t("Services.Form.Rate", { value: "RACD solidarité" })}
                        items={sacdOptions}
                        error={errors.sacd}
                        required
                    />
                </div>
            </CSSTransition>

            {!ircecApplies && <span className={styles["ircec-info"]}>{t("Services.Form.URSSAFInfo")}</span>}
            <StyledButton buttonType="primary" type="submit" loading={isSubmitting}>
                {isUpdating ? t("Services.Form.UpdateService") : t("Services.Form.CreateService")}
            </StyledButton>
        </form>
    );
};

export default ServicesForm;
